import { usePresencePlayers } from '../../../components/SessionPresence';
import React, {
  useEffect,
  useMemo,
  useState,
  Key,
  useContext,
  ReactNode,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Badge,
  Space,
  Table,
  Switch,
  Typography,
  Button,
  Tooltip,
  Divider,
  Empty,
  Popover,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { TableColumnGroupType as ColumnGroupType, TableColumnType as ColumnType } from 'antd';
import { CSVLink } from 'react-csv';
import ValuationRefresh from './ValuationRefresh';

import {
  selectors,
  Player,
  Metrics,
  utils,
  GROUP_ASSET_NAME,
  PlayerAllocations,
  GROUP_OF_ASSET,
  GROUP_ASSET_INDEX,
  CREATING,
  CASH_APPROACH,
  initializeForwardContractsState,
  WEALTH_MANAGEMENT,
  fetchPlayersSAAdecisions,
  fetchPlayersClientDealingStats,
} from 'state';
import { formatters, useModal } from 'services';
import { columnSearchWidget, LoadingScreen } from 'components';
import PlayerModal from './PlayerDetails/PlayerModal';
import { green, red, yellow } from '@ant-design/colors';
import AssetGroupAllocations from '../SessionSettings/AssetGroupAllocations';
import { TrainerSessionContext } from '../TrainerSessionContext';
import {
  clientStatsFetched,
  hasAlternativesGroup,
  hasBondsGroup,
  hasCryptoGroup,
  hasEquitiesGroup,
  sessionHasAFSassetsOverview,
} from '../../../state/app/selectors';
import {
  metricsPercent1Digit,
  metricsPercent2Digit,
} from 'services/formatters';
import CheckSAAdecisions from './CheckSAAdecisionsModal';
import { getPlayerNameDisplay, PlayerNameDisplay } from './playerNameDisplay';

const ClientDealingOverviewTable: React.FC = () => {
  const dispatch = useDispatch();
  const trainerContext = useContext(TrainerSessionContext);

  const [selectedPlayer, setSelectedPlayer] = useState<number | undefined>();
  const [checkSAAdecisionsModal, showCheckSAAdecisionsModal] =
    useModal(CheckSAAdecisions);
  const session = useSelector(selectors.activeSession);
  const fetchedClientStats = useSelector(selectors.clientStatsFetched);

  useEffect(() => {
    if (!fetchedClientStats) {
      dispatch(fetchPlayersClientDealingStats({ session_id: session.id }));
    }
  }, []);

  const reportingCurrency = utils.useReportingCurrency(session.id);
  const players = usePresencePlayers(useSelector(selectors.playersList));
  const allPersons = useSelector(selectors.getAllPersons);

  if (!fetchedClientStats) {
    return <LoadingScreen />;
  }

  const selectionType = session.allow_individual_benchmark
    ? 'radio'
    : 'checkbox';

  const defaultAllocations: PlayerAllocations = {
    Cash: 0,
    Equities: 0,
    Bonds: 0,
    Alternatives: 0,
    FX: 0,
    Crypto: 0,
  };

  type PlayerData = Player & Metrics & PlayerAllocations & PlayerNameDisplay;
  const dataa: PlayerData[] = players.map(player => {
    const allocations = player.tactical_asset_allocations.reduce((acc, p) => {
      const group = GROUP_OF_ASSET[p.asset_class];
      const groupName = GROUP_ASSET_NAME[group] as keyof PlayerAllocations;
      const prev = acc[groupName] ?? 0;
      const obj = { ...acc, [groupName]: prev + p.percent };
      return obj;
    }, defaultAllocations);
    const finalObj = {
      ...allocations,
      ...player.last_metrics,
      ...player,
      ...getPlayerNameDisplay(player, allPersons, trainerContext.showPersonNames),
    };
    return finalObj;
  });

  const activePlayersData = dataa.filter(player => player.deals !== 0);

  const getCurrencySymbol = (locale: string, currency: string) =>
    (0)
      .toLocaleString(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, '')
      .trim();

  const currencySymbolCharacter = getCurrencySymbol(
    'en-US',
    reportingCurrency.symbol
  );

  const columns: ((ColumnGroupType<PlayerData> | ColumnType<PlayerData>) & {
    dataIndex: string;
  })[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      fixed: 'left',
      width: 240,
      render: (name, record) => (
        <p
          style={{
            maxWidth: 230,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          <Badge status={record.online ? 'success' : 'default'} />
          {record.displayName}
          {record.displayName !== name && (
            <Typography.Text
              type="secondary"
              style={{ display: 'block', fontSize: 12 }}
            >
              {name}
            </Typography.Text>
          )}
        </p>
      ),
      sorter: (a, b) =>
        a.displayName < b.displayName ? -1 : a.displayName > b.displayName ? 1 : 0,
      ...columnSearchWidget({ dataIndex: 'nameSearchText' }),
    },
    {
      title: 'MktVal (M, ' + currencySymbolCharacter + ')',
      dataIndex: 'last_market_value',
      fixed: 'left',
      width: 0,

      align: 'right',
      render: (value, player) => (
        <>
          {formatters.millionNoUnit(value)}
          <ValuationRefresh player={player} />
        </>
      ),
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.last_market_value - b.last_market_value,
    },
    {
      title: 'Interbank Amount (M, ' + currencySymbolCharacter + ')',
      dataIndex: 'interbank_amount',
      fixed: 'left',
      width: 130,

      align: 'right',
      render: formatters.millionNoUnit,
      // defaultSortOrder: 'descend',
      sorter: (a, b) => a.interbank_amount - b.interbank_amount,
    },
    {
      title: 'Interbank Interest (' + currencySymbolCharacter + ')',
      dataIndex: 'interbank_interest',
      fixed: 'left',
      width: 109,
      align: 'right',
      render: formatters.commasNoDigits,
      sorter: (a, b) => a.interbank_interest - b.interbank_interest,

      // render: formatters.millionNoUnitExact2Digits,
      // defaultSortOrder: 'descend',
      // sorter: {
      //   compare: (a, b) => a.last_pnl_sum - b.last_pnl_sum,
      //   multiple: 1,
      // },
      // sorter: (a, b) => a.last_pnl_sum - b.last_pnl_sum,
    },
    {
      title: 'Emergency Interest (' + currencySymbolCharacter + ')',
      dataIndex: 'paid_lombard_interest',
      fixed: 'left',
      width: 130,
      align: 'right',
      render: formatters.commasNoDigits,
      sorter: (a, b) => a.paid_lombard_interest - b.paid_lombard_interest,

      // render: formatters.millionNoUnit,
      // defaultSortOrder: 'descend',
      // sorter: {
      //   compare: (a, b) => a.last_fx_return - b.last_fx_return,
      //   multiple: 1,
      // },
      // sorter: (a, b) => a.last_fx_return - b.last_fx_return,
    },
    {
      title: 'Repo Interest (' + currencySymbolCharacter + ')',
      dataIndex: 'paid_repo_interest',
      fixed: 'left',
      width: 85,
      align: 'right',
      render: formatters.commasNoDigits,
      sorter: (a, b) => a.paid_repo_interest - b.paid_repo_interest,

      // render: formatters.percent2Digit,
      // sorter: (a, b) => a.last_portfolio_return - b.last_portfolio_return,
    },
    {
      title: 'Client Interest (M' + currencySymbolCharacter + ')',
      dataIndex: 'client_interest',
      fixed: 'left',
      width: 85,
      align: 'right',
      render: formatters.millionNoUnit,
      sorter: (a, b) => a.client_interest - b.client_interest,

      // render: formatters.percent2Digit,
      // sorter: (a, b) => a.last_portfolio_return - b.last_portfolio_return,
    },
    {
      title: 'Loans (M' + currencySymbolCharacter + ')',
      dataIndex: 'loans',
      fixed: 'left',
      width: 85,
      align: 'right',
      render: formatters.millionNoUnit,
      sorter: (a, b) => a.loans - b.loans,

      // render: formatters.percent2Digit,
      // sorter: (a, b) => a.last_portfolio_return - b.last_portfolio_return,
    },
    {
      title: 'Depos (M' + currencySymbolCharacter + ')',
      dataIndex: 'depos',
      fixed: 'left',
      width: 85,
      align: 'right',
      render: formatters.millionNoUnit,
      sorter: (a, b) => a.depos - b.depos,

      // render: formatters.percent2Digit,
      // sorter: (a, b) => a.last_portfolio_return - b.last_portfolio_return,
    },
  ];
  // if (!HTMorAFS) {
  //   columns.push({
  //     title: 'VaR (M, ' + currencySymbolCharacter + ')',
  //     dataIndex: 'var05',
  //     align: 'right',
  //     render: formatters.millionNoUnit,
  //     sorter: (a, b) => a.var05 - b.var05,
  //   });
  // }

  const numberOfAllocationColumns = 0;

  const actionsWidth = 61;

  // const csvData = useMemo(() => {
  const csvData = () => {
    const titles = columns.map(col => {
      if (col.title !== 'Actions') return col.title;
    });

    const body = dataa.map((player, index) => {
      return columns.map(col => {
        if (col.dataIndex === 'actions') return;
        const value = player[col.dataIndex as keyof typeof player];
        if (col.dataIndex === 'last_market_value') {
          return formatters.millionNoUnit(value as number);
        }
        if (col.dataIndex === 'name') return value;
        const toWrite = col.render ? col.render(value, player, index)! : value!;
        if (typeof toWrite === 'object' && col.render) {
          return formatters.percent2Digit(value as number);
        } else {
          return col.render ? col.render(value, player, index) : value;
        }
      });
    });

    body.unshift(titles as any);

    return body;
  };
  // }, [columns, dataa]);

  const csvFilename = `Session_${session.id}_Overview_${new Date()
    .toISOString()
    .replaceAll(/\.([0-9]*)/g, '')}`;

  const glanceGroupAllocations = <AssetGroupAllocations />;

  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          {trainerContext.showOnlyActivePlayers && session.status !== CREATING
            ? 'No active players.'
            : 'Please finish creating the session, then players and their stats will\n' +
              '          appear here.'}
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };

  const playerAllocationCssProperty =
    actionsWidth + 91 * numberOfAllocationColumns - 217;
  const minTableWidth = 890 + actionsWidth + 91 * numberOfAllocationColumns;

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    trainerContext.setSelectedRows(newSelectedRowKeys);
    // if trainer intervenes and makes a selection, don't reset it to top 3
    trainerContext.setStopAutoSelectingTop3Players(true);
  };
  const selectedRowKeys = trainerContext.selectedRows;
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    // getCheckboxProps: (record: LogsType) => ({
    //   disabled: !('id' in record), // Column configuration not to be checked
    // }),
  };

  return (
    <div style={{ marginTop: 30 }}>
      <Table
        rowKey="id"
        dataSource={
          !trainerContext.showOnlyActivePlayers ? dataa : activePlayersData
        }
        columns={columns}
        rowSelection={{
          type: selectionType,
          ...rowSelection,
        }}
        pagination={false}
        bordered
        style={{ marginTop: 15, minWidth: minTableWidth }}
        // scroll={{ x: true }}
        locale={noDataBehaviour}
        footer={() => {
          return (
            <div style={{ position: 'relative' }}>
              <Switch
                checked={trainerContext.showOnlyActivePlayers}
                onChange={checked => {
                  trainerContext.setShowOnlyActivePlayers(checked);
                }}
                style={{ marginLeft: 20, marginBottom: 5 }}
              />
              <p style={{ marginLeft: '8px', display: 'inline' }}>
                Only show active accounts{' '}
                <Tooltip title="Accounts with at least 1 transaction">
                  <InfoCircleOutlined />
                </Tooltip>
              </p>
              <Switch
                aria-label="Show person names"
                checked={trainerContext.showPersonNames}
                onChange={trainerContext.setShowPersonNames}
                style={{ marginLeft: 20, marginBottom: 5 }}
              />
              <span style={{ marginLeft: 8 }}>
                Show person names{' '}
                <Tooltip title="People linked to each account. Online status applies to the shared account.">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
              <Button style={{ position: 'absolute', right: 0, bottom: -2 }}>
                <CSVLink data={csvData()} filename={csvFilename}>
                  Download CSV
                </CSVLink>
              </Button>
            </div>
          );
        }}
      />
      {checkSAAdecisionsModal}
      {selectedPlayer && (
        <PlayerModal
          playerId={selectedPlayer}
          hide={() => {
            setSelectedPlayer(0);
            // dispatch(invalidateForwardContracts({}));
          }}
        />
      )}
    </div>
  );
};

export default ClientDealingOverviewTable;
